import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { formatPrice, hasSplitPayment, formatSplitPaymentInfo } from "../utils/formatters";

const PaymentSelector = ({ 
  lessonData, 
  signInMethod, 
  onMethodSelect,
  onBuyPassClick,
  passExpired = false
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isSplitPayment = hasSplitPayment(lessonData);
  const splitInfo = isSplitPayment ? formatSplitPaymentInfo(lessonData.online_payment_amount, lessonData.on_site_payment_amount) : null;

  return (
    <Box>
      {/* Expired Pass Warning */}
      {passExpired && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
          <Typography variant="body2" fontWeight="500" gutterBottom>
            Twój karnet wygasł
          </Typography>
          <Typography variant="caption">
            Nie możesz użyć karnetu do tych zajęć. Wybierz płatność jednorazową lub kup nowy karnet.
          </Typography>
        </Alert>
      )}
      {/* Split Payment Information */}
      {isSplitPayment && (
        <Card elevation={0} sx={{ backgroundColor: '#e3f2fd', border: '1px solid #2196f3', mb: 2 }}>
          <CardContent>
            <Stack spacing={1.5}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ fontSize: '1.5rem' }}>💰</Box>
                <Typography variant="body2" fontWeight="600">
                  Płatność podzielona
                </Typography>
              </Box>
              <Stack direction="row" justifyContent="space-around" flexWrap="wrap" gap={1}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    Online (teraz)
                  </Typography>
                  <Typography variant="body1" fontWeight="bold" color="primary">
                    {splitInfo.online}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    Na miejscu
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {splitInfo.onsite}
                  </Typography>
                </Box>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      )}

      <Typography variant="body2" fontWeight="600" gutterBottom sx={{ mb: 1.5 }}>
        💳 Wybierz opcję płatności
      </Typography>
      <Stack 
        direction={isMobile ? "column" : "row"}
        spacing={1.5}
        sx={{ width: '100%' }}
      >
        <Button
          variant={signInMethod === "pojedyncza" ? "contained" : "outlined"}
          color="primary"
          onClick={() => onMethodSelect("pojedyncza")}
          fullWidth
          sx={{ 
            minHeight: 44,
            justifyContent: 'center',
            textAlign: 'center'
          }}
        >
          <Box>
            <Typography variant="body2" fontWeight="600">
              {isSplitPayment 
                ? `${lessonData.online_payment_amount} zł`
                : lessonData.lesson_type?.price 
                  ? formatPrice(lessonData.lesson_type.price)
                  : 'Pojedyncza lekcja'
              }
            </Typography>
            {!isSplitPayment && (
              <Typography variant="caption">
                Pojedyncza lekcja
              </Typography>
            )}
          </Box>
        </Button>
        {!isSplitPayment && (
          <Button
            variant={signInMethod === "kup_karnet" ? "contained" : "outlined"}
            color="primary"
            onClick={() => {
              onMethodSelect("kup_karnet");
              onBuyPassClick();
            }}
            fullWidth
            sx={{ minHeight: 44 }}
          >
            <Box>
              <Typography variant="body2" fontWeight="600">
                Kup Karnet
              </Typography>
              <Typography variant="caption">
                Więcej zajęć
              </Typography>
            </Box>
          </Button>
        )}
      </Stack>
    </Box>
  );
};

export default PaymentSelector;
